import React, { useCallback, useEffect, useState } from 'react';

// Fillable PDFs (AcroForm). pdf.js renders the fields as real inputs when <Page>
// gets `renderForms`, and keeps what visitors type in pdf.annotationStorage,
// keyed by annotation id, so values survive pages being unmounted by the
// viewer's virtualization. This module reads the form's structure, collects the
// values by field name, and submits them to the owner (see DocumentFormView).
//
// Django puts data-formurl / data-formtoken on #root for tracked visits,
// data-forms="off" unless the owner turned on "Enable submissions",
// data-formpreview="1" in owner previews, and data-formsubmitbutton when the
// PDF has its own Submit button (pdf_forms.find_submit_button).
//
// With submissions on, answers are sent either from our Submit bar (FormBar)
// or, when the PDF has its own Submit button, from that button: a transparent
// hotspot over it (SubmitHotspots) runs the same submit, with a small toast
// (FormToast) for the result. With submissions off the fields are still
// fillable, and the toolbar's download saves the filled copy (saveFilledCopy).
//
// Signature fields: pdf.js renders nothing interactive for them, so the viewer
// draws its own "Sign here" box over each one (SignatureBoxes) and opens
// SignaturePad. A signature is submitted as {type: 'signature', image (PNG data
// URL), signer_name, method}, with the consent text the signer agreed to.

const SKIPPED_TYPES = new Set(['button', 'signature', '']);

export const DEFAULT_SIGN_CONSENT = 'I agree to sign this document electronically, and that my electronic signature is the legal equivalent of my handwritten signature.';

export const readFormConfig = (dataset, params) => ({
    url: dataset.formurl || params.get('formurl') || '',
    token: dataset.formtoken || params.get('formtoken') || '',
    off: (dataset.forms || params.get('forms')) === 'off',
    preview: (dataset.formpreview || params.get('formpreview')) === '1',
    signConsent: dataset.signconsent || params.get('signconsent') || DEFAULT_SIGN_CONSENT,
    signerName: dataset.signername || params.get('signername') || '',
    submitButton: dataset.formsubmitbutton || params.get('formsubmitbutton') || '',
    labels: parseLabels(dataset.formlabels || params.get('formlabels')),
});

// data-formlabels: {field name: readable label} from the server (the owner's
// renames, else what it worked out from the PDF).
function parseLabels(raw) {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
        return {};
    }
}

const SUBMIT_NAME_RE = /submit|send/i;

// The PDF's own Submit button among its push buttons: the one the server found
// (a SubmitForm action), else one named or labelled like Submit / Send.
const pickSubmitButton = (buttons, preferred) => {
    const placed = buttons.filter((b) => b.widgets.length);
    if (preferred) {
        const exact = placed.find((b) => b.name === preferred);
        if (exact) return exact;
    }
    return placed.find((b) => SUBMIT_NAME_RE.test(b.name.split('.').pop()) || SUBMIT_NAME_RE.test(b.label || '')) || null;
};

const toBox = (viewport, rect) => {
    // PDF user space -> page pixels at scale 1 (handles rotation and crop box).
    const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(rect);
    return { left: Math.min(x1, x2), top: Math.min(y1, y2), width: Math.abs(x2 - x1), height: Math.abs(y2 - y1) };
};

// { fields: [{ name, type, widgets, required, label, page, ... }],
//   signatures: [{ name, kind: 'signature', widgets: [{ id, page, box }], required, label, page }],
//   submitButton: { name, label, widgets: [{ id, page, box }] } or null }
// or null when the PDF has neither fillable fields nor signature fields.
// A widget's `box` is its rectangle in page pixels at scale 1
// (left/top/width/height), ready to multiply by the render scale.
export const loadFormFields = async (pdf, { submitButton: preferredButton = '', labels = {} } = {}) => {
    let objects;
    try {
        objects = await pdf.getFieldObjects();
    } catch (_) {
        return null;
    }
    if (!objects) return null;
    const fields = [];
    const signatures = [];
    const buttons = [];
    Object.keys(objects).forEach((name) => {
        const pushWidgets = (objects[name] || []).filter((w) => w && w.id && w.type === 'button');
        if (pushWidgets.length) {
            buttons.push({ name, label: '', widgets: pushWidgets.map((w) => ({ id: w.id, page: typeof w.page === 'number' ? w.page : 0, box: null })) });
        }
        const sigWidgets = (objects[name] || []).filter((w) => w && w.id && w.type === 'signature');
        if (sigWidgets.length) {
            signatures.push({
                name,
                kind: 'signature',
                widgets: sigWidgets.map((w) => ({ id: w.id, page: typeof w.page === 'number' ? w.page : 0, box: null })),
                page: typeof sigWidgets[0].page === 'number' ? sigWidgets[0].page : 0,
                required: false,
                label: '',
            });
        }
        const widgets = (objects[name] || []).filter((w) => w && w.id && !SKIPPED_TYPES.has(w.type));
        if (!widgets.length) return;
        const first = widgets[0];
        if (first.type === 'text' && first.password) return; // never collect passwords
        fields.push({
            name,
            type: first.type,
            widgets,
            page: typeof first.page === 'number' ? first.page : 0,
            hidden: widgets.every((w) => w.hidden),
            editable: widgets.some((w) => w.editable !== false),
            multiple: Boolean(first.multipleSelection),
            required: false,
            label: '',
        });
    });
    const visibleFields = fields.filter((f) => !f.hidden && f.editable);
    if (!visibleFields.length && !signatures.length) return null;

    // `required`, the tooltip label and (for signatures) the rectangle live on
    // the page annotations, not on the field objects; only the pages that hold
    // fields are read.
    const byId = {};
    const boxedById = {}; // signature and push-button widgets, which need their rectangle
    fields.forEach((f) => f.widgets.forEach((w) => { byId[w.id] = f; }));
    signatures.forEach((f) => f.widgets.forEach((w) => { byId[w.id] = f; boxedById[w.id] = w; }));
    buttons.forEach((b) => b.widgets.forEach((w) => { byId[w.id] = b; boxedById[w.id] = w; }));
    const pages = Array.from(new Set(fields.map((f) => f.page)
        .concat(signatures.flatMap((f) => f.widgets.map((w) => w.page)))
        .concat(buttons.flatMap((b) => b.widgets.map((w) => w.page)))));
    await Promise.all(pages.map(async (index) => {
        try {
            const page = await pdf.getPage(index + 1);
            const annotations = await page.getAnnotations({ intent: 'display' });
            const viewport = page.getViewport({ scale: 1 });
            annotations.forEach((a) => {
                const field = byId[a.id];
                if (!field) return;
                if (a.required) field.required = true;
                if (a.alternativeText && !field.label) field.label = a.alternativeText;
                const widget = boxedById[a.id];
                if (widget && a.rect) widget.box = toBox(viewport, a.rect);
            });
        } catch (_) { /* a page we can't read just loses the required flag */ }
    }));
    // The server's labels win over the PDF's tooltips.
    fields.concat(signatures).forEach((f) => {
        if (typeof labels[f.name] === 'string' && labels[f.name]) f.label = labels[f.name];
    });
    const hasBox = (w) => w.box && w.box.width > 0 && w.box.height > 0;
    signatures.forEach((f) => { f.widgets = f.widgets.filter(hasBox); });
    buttons.forEach((b) => { b.widgets = b.widgets.filter(hasBox); });
    const placed = signatures.filter((f) => f.widgets.length);
    if (!visibleFields.length && !placed.length) return null;
    fields.sort((a, b) => a.page - b.page);
    placed.sort((a, b) => a.page - b.page);
    return { fields, signatures: placed, submitButton: pickSubmitButton(buttons, preferredButton) };
};

// Whether the visitor has typed or ticked anything (pdf.js keeps it in
// annotationStorage), so the toolbar download should save their copy.
export const hasFilledValues = (pdf) => {
    try {
        return Boolean(pdf && pdf.annotationStorage && pdf.annotationStorage.size > 0);
    } catch (_) {
        return false;
    }
};

// What the visitor set for one widget, or undefined if untouched. (getValue's
// default argument is Object.assign'd into, so it can't be used to test "unset".)
const stored = (storage, id) => {
    try {
        return storage.getRawValue ? storage.getRawValue(id) : storage.getValue(id, {});
    } catch (_) {
        return undefined;
    }
};

// Current value of one field: text -> string, checkbox -> true/false (or the
// chosen export value for a group of differently-valued boxes), radio -> the
// selected export value, list -> string (or array when multi-select).
export const fieldValue = (pdf, field) => {
    const storage = pdf.annotationStorage;
    const { type, widgets } = field;
    if (type === 'text') {
        const s = stored(storage, widgets[0].id);
        const v = s && s.value !== undefined ? s.value : widgets[0].value;
        return v == null ? '' : String(v);
    }
    if (type === 'checkbox' || type === 'radiobutton') {
        const exportValues = Array.from(new Set(widgets.map((w) => w.exportValues).filter(Boolean)));
        let chosen = null;
        widgets.forEach((w) => {
            const s = stored(storage, w.id);
            const on = s && s.value !== undefined
                ? Boolean(s.value)
                : (w.value && w.value !== 'Off' && w.value === w.exportValues);
            if (on && chosen == null) chosen = w.exportValues || true;
        });
        if (type === 'checkbox' && exportValues.length <= 1) return chosen != null;
        return chosen == null || chosen === true ? '' : String(chosen);
    }
    if (type === 'combobox' || type === 'listbox') {
        const s = stored(storage, widgets[0].id);
        let v = s && s.value !== undefined ? s.value : widgets[0].value;
        if (Array.isArray(v)) {
            v = v.map((x) => (x && typeof x === 'object' ? x.exportValue || x.displayValue : x)).filter((x) => x != null).map(String);
            return field.multiple ? v : (v[0] || '');
        }
        if (v == null) return field.multiple ? [] : '';
        return field.multiple ? [String(v)] : String(v);
    }
    return '';
};

const isEmpty = (v) => v === '' || v === false || (Array.isArray(v) && !v.length);

// Values by field name, plus the first required field left empty (in page
// order, signatures included). `signed` is {field name: signature value}.
export const collectForm = (pdf, form, signed = {}) => {
    const values = {};
    const missingAll = [];
    form.fields.forEach((field) => {
        if (field.hidden) return;
        const v = fieldValue(pdf, field);
        values[field.name] = v;
        if (field.required && field.editable && isEmpty(v)) missingAll.push(field);
    });
    let anySigned = false;
    (form.signatures || []).forEach((field) => {
        const v = signed[field.name];
        if (v) {
            values[field.name] = v;
            anySigned = true;
        } else if (field.required) {
            missingAll.push(field);
        }
    });
    missingAll.sort((a, b) => a.page - b.page);
    return { values, missing: missingAll[0] || null, anySigned };
};

export const fieldLabel = (field) => {
    if (field.label) return field.label;
    if (field.kind === 'signature') return 'Signature';
    const last = field.name.split('.').pop();
    return last.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim() || 'this field';
};

const downloadBlob = (bytes, filename) => {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const safeFilename = (title) => {
    const base = (title || document.title || 'form').replace(/[\\/:*?"<>|]+/g, ' ').trim().slice(0, 80) || 'form';
    return base.toLowerCase().endsWith('.pdf') ? base.replace(/\.pdf$/i, ' (filled).pdf') : base + ' (filled).pdf';
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The server builds the signed PDF just after the submit, so the link can 404
// for a few seconds: retry quietly, then hand over the file.
const fetchSignedCopy = async (url, title) => {
    for (let attempt = 0; attempt < 20; attempt++) {
        const resp = await fetch(url, { credentials: 'same-origin' });
        if (resp.ok) {
            const bytes = await resp.arrayBuffer();
            const base = (title || document.title || 'document').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\.pdf$/i, '').trim().slice(0, 80) || 'document';
            downloadBlob(bytes, base + ' (signed).pdf');
            return true;
        }
        if (resp.status !== 404 && resp.status !== 202 && resp.status !== 409) return false;
        await sleep(attempt < 5 ? 1000 : 2500);
    }
    return false;
};

export const saveFilledCopy = async (pdf, title) => {
    const bytes = await pdf.saveDocument();
    downloadBlob(bytes, safeFilename(title));
};

// The submit flow, shared by the Submit bar and the PDF's own Submit button:
// validation (required fields, signatures), the POST, and the signed copy.
export const useFormSubmit = ({ pdf, form, config, title, signed, onMissing, onSubmitted }) => {
    const [state, setState] = useState('idle'); // idle | sending | sent | error
    const [message, setMessage] = useState('');
    const [signedUrl, setSignedUrl] = useState('');
    const [copyState, setCopyState] = useState('idle'); // idle | preparing | failed
    const canSubmit = Boolean(config.url && config.token) && !config.off;

    useEffect(() => {
        // Typing again after an error clears it.
        if (state !== 'error') return undefined;
        const reset = (e) => {
            if (e.target && e.target.closest && e.target.closest('.annotationLayer')) { setState('idle'); setMessage(''); }
        };
        document.addEventListener('input', reset, true);
        return () => document.removeEventListener('input', reset, true);
    }, [state]);

    useEffect(() => {
        // Signing after a "please sign" error clears the error.
        if (state === 'error') { setState('idle'); setMessage(''); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signed]);

    const submit = useCallback(async () => {
        if (!canSubmit) {
            if (config.preview) { setState('error'); setMessage('Preview: submissions are off'); }
            return;
        }
        if (state === 'sending') return;
        const { values, missing, anySigned } = collectForm(pdf, form, signed);
        if (missing) {
            setState('error');
            setMessage(missing.kind === 'signature'
                ? 'Please sign where it says “Sign here”.'
                : 'Please fill in “' + fieldLabel(missing) + '”.');
            onMissing && onMissing(missing);
            return;
        }
        setState('sending');
        setMessage('');
        const payload = { token: config.token, fields: values };
        if (anySigned) {
            payload.consent = { text: config.signConsent || DEFAULT_SIGN_CONSENT, agreed: true };
            let tz = '';
            try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { /* old browsers */ }
            payload.client = { tz, time: new Date().toISOString() };
        }
        try {
            const resp = await fetch(config.url, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
                body: JSON.stringify(payload),
                credentials: 'same-origin',
            });
            let body = {};
            try { body = await resp.json(); } catch (_) { /* empty body */ }
            if (resp.ok) {
                setState('sent');
                setMessage(body.message || (anySigned ? 'Signed and sent. Thank you!' : 'Sent. Thank you!'));
                setSignedUrl(body.signed_pdf_url || '');
                setCopyState('idle');
                onSubmitted && onSubmitted();
            } else if (resp.status === 429) {
                setState('error');
                setMessage('Too many submissions, try again later.');
            } else {
                setState('error');
                setMessage(body.error || 'Couldn’t send the form. Please try again.');
            }
        } catch (_) {
            setState('error');
            setMessage('Couldn’t reach the server. Check your connection and try again.');
        }
    }, [canSubmit, state, pdf, form, config, signed, onMissing, onSubmitted]);

    const downloadSigned = useCallback(async () => {
        if (!signedUrl || copyState === 'preparing') return;
        setCopyState('preparing');
        let ok = false;
        try { ok = await fetchSignedCopy(signedUrl, title); } catch (_) { ok = false; }
        setCopyState(ok ? 'idle' : 'failed');
    }, [signedUrl, copyState, title]);

    const reset = useCallback(() => { setState('idle'); setMessage(''); setSignedUrl(''); setCopyState('idle'); }, []);
    const dismiss = useCallback(() => { setState('idle'); setMessage(''); }, []);

    return { state, message, signedUrl, copyState, canSubmit, submit, downloadSigned, reset, dismiss };
};

const statusNote = (sub) => {
    if (sub.state === 'sent' && sub.copyState === 'preparing') return 'Preparing your copy…';
    if (sub.state === 'sent' && sub.copyState === 'failed') return 'Your copy isn’t ready yet. Try again in a minute.';
    return sub.message;
};

const statusColor = (state) => (state === 'error' ? '#b91c1c' : state === 'sent' ? '#047857' : '#334155');

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// The sticky bar under the pages, when submissions are on and the PDF has no
// Submit button of its own.
export const FormBar = ({ form, config, brandColor, sub }) => {
    const color = brandColor || '#2563eb';
    const hasSignatures = Boolean(form.signatures && form.signatures.length);
    const onlySignatures = hasSignatures && !form.fields.some((f) => !f.hidden && f.editable);
    let note = onlySignatures ? 'Sign where it says “Sign here”, then submit' : (hasSignatures ? 'Fill in the form and sign, then submit' : 'Fill in the form, then submit');
    if (config.preview && !sub.canSubmit) note = 'Preview: submissions are off';
    if (sub.state === 'sent' || sub.state === 'error') note = statusNote(sub);
    const disabled = !sub.canSubmit || sub.state === 'sending';

    return (
        <div
            role="region"
            aria-label="Form"
            style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px 12px',
                padding: '10px 16px',
                paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
                background: '#fff',
                borderTop: '1px solid #e2e8f0',
                boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
                fontFamily: FONT,
                fontSize: 14,
                zIndex: 3,
            }}
        >
            <span
                aria-live="polite"
                style={{
                    color: statusColor(sub.state),
                    fontWeight: sub.state === 'idle' || sub.state === 'sending' ? 400 : 600,
                    flex: '1 1 200px',
                    textAlign: 'center',
                    maxWidth: 520,
                }}
            >
                {sub.state === 'sent' ? '✓ ' : ''}{note}
            </span>
            <span style={{ display: 'inline-flex', gap: 8 }}>
                {sub.state === 'sent' && sub.signedUrl && (
                    <button type="button" onClick={sub.downloadSigned} disabled={sub.copyState === 'preparing'} style={{ ...secondaryBtn, opacity: sub.copyState === 'preparing' ? 0.6 : 1 }}>
                        {sub.copyState === 'preparing' ? 'Preparing your copy…' : 'Download signed copy'}
                    </button>
                )}
                {sub.state === 'sent' && (
                    <button type="button" onClick={sub.reset} style={secondaryBtn}>
                        Edit and send again
                    </button>
                )}
                {sub.state !== 'sent' && (
                    <button
                        type="button"
                        onClick={sub.submit}
                        disabled={disabled}
                        style={{ ...primaryBtn, background: color, opacity: disabled ? 0.55 : 1, cursor: !sub.canSubmit ? 'not-allowed' : 'pointer' }}
                    >
                        {sub.state === 'sending' ? 'Sending…' : 'Submit'}
                    </button>
                )}
            </span>
        </div>
    );
};

// When the PDF's own Submit button sends: a small toast, bottom centre, while
// sending and with the result. Nothing shows until the button is pressed.
export const FormToast = ({ sub }) => {
    if (sub.state === 'idle') return null;
    const note = sub.state === 'sending' ? 'Sending…' : statusNote(sub);
    return (
        <div
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                left: '50%',
                bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
                transform: 'translateX(-50%)',
                zIndex: 60,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '8px 12px',
                maxWidth: 'calc(100vw - 32px)',
                padding: '10px 14px',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                boxShadow: '0 10px 30px rgba(15,23,42,0.18)',
                fontFamily: FONT,
                fontSize: 14,
            }}
        >
            <span style={{ color: statusColor(sub.state), fontWeight: sub.state === 'sending' ? 400 : 600 }}>
                {sub.state === 'sent' ? '✓ ' : ''}{note}
            </span>
            {sub.state === 'sent' && sub.signedUrl && (
                <button type="button" onClick={sub.downloadSigned} disabled={sub.copyState === 'preparing'} style={{ ...secondaryBtn, padding: '6px 12px', opacity: sub.copyState === 'preparing' ? 0.6 : 1 }}>
                    {sub.copyState === 'preparing' ? 'Preparing your copy…' : 'Download signed copy'}
                </button>
            )}
            {sub.state !== 'sending' && (
                <button type="button" aria-label="Dismiss" onClick={sub.dismiss} style={{ border: 'none', background: 'none', color: '#64748b', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: '0 2px' }}>×</button>
            )}
        </div>
    );
};

// The PDF's own Submit button, made to send through us: a transparent button
// over each of its widgets on this page (rendered inside react-pdf's <Page>,
// like SignatureBoxes).
export const SubmitHotspots = ({ form, pageIndex, scale, onSubmit }) => {
    const button = form && form.submitButton;
    if (!button) return null;
    return button.widgets.filter((w) => w.page === pageIndex).map((w) => (
        <button
            key={w.id}
            type="button"
            data-submit-widget={w.id}
            aria-label={button.label || 'Submit'}
            title={button.label || 'Submit'}
            onClick={(e) => { e.stopPropagation(); onSubmit(); }}
            style={{
                position: 'absolute',
                left: w.box.left * scale,
                top: w.box.top * scale,
                width: w.box.width * scale,
                height: w.box.height * scale,
                zIndex: 5,
                padding: 0,
                margin: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
            }}
        />
    ));
};

const primaryBtn = {
    border: 'none',
    color: '#fff',
    fontWeight: 600,
    fontSize: 14,
    padding: '9px 18px',
    borderRadius: 8,
    cursor: 'pointer',
};

const secondaryBtn = {
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#0f172a',
    fontWeight: 500,
    fontSize: 14,
    padding: '8px 14px',
    borderRadius: 8,
    cursor: 'pointer',
};

// "Sign here" boxes for one page, rendered as children of react-pdf's <Page>
// so they sit in the page's own coordinate space: they scale with the
// viewer's CSS zoom and mount/unmount with the page under virtualization.
// `scale` is the page's render scale; widget boxes are at scale 1.
export const SignatureBoxes = ({ form, pageIndex, scale, signed, brandColor, onOpen }) => {
    if (!form || !form.signatures || !form.signatures.length) return null;
    const color = brandColor || '#2563eb';
    const boxes = [];
    form.signatures.forEach((field) => {
        field.widgets.forEach((w) => {
            if (w.page === pageIndex) boxes.push({ field, widget: w });
        });
    });
    if (!boxes.length) return null;
    return boxes.map(({ field, widget }) => {
        const value = signed && signed[field.name];
        const { left, top, width, height } = widget.box;
        const h = height * scale;
        return (
            <button
                key={widget.id}
                type="button"
                data-sig-widget={widget.id}
                data-sig-field={field.name}
                aria-label={value ? `Signed by ${value.signer_name}. Change signature` : 'Sign here'}
                onClick={(e) => { e.stopPropagation(); onOpen(field); }}
                className={'pdfdeck-sig-box' + (value ? ' is-signed' : '')}
                style={{
                    position: 'absolute',
                    left: left * scale,
                    top: top * scale,
                    width: width * scale,
                    height: h,
                    zIndex: 5,
                    boxSizing: 'border-box',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    border: value ? '1px solid transparent' : `2px dashed ${color}`,
                    borderRadius: 4,
                    background: value ? 'transparent' : 'rgba(37,99,235,0.07)',
                    color,
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontWeight: 600,
                    fontSize: Math.max(10, Math.min(22, h * 0.38)),
                    overflow: 'hidden',
                }}
            >
                {value ? (
                    <>
                        <img src={value.image} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
                        <span className="pdfdeck-sig-change" style={{ position: 'absolute', right: 2, bottom: 2, fontSize: Math.max(9, Math.min(13, h * 0.22)), background: '#fff', color, borderRadius: 4, padding: '1px 5px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>Change</span>
                    </>
                ) : (
                    <>
                        <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
                        </svg>
                        Sign here
                    </>
                )}
            </button>
        );
    });
};
